/**
 * Mutations over a KBStore. Pure and synchronous: each takes a store and
 * returns a new one.
 *
 * This module holds no connection of its own. The hook pulls from Postgres
 * and seeds the in-memory cache below; the sync bridge pushes writes back.
 * Read-only views live in ./queries.ts.
 */

import { localeUsage, localeRemovalBlocker } from "../shared/localeUsage"
import { isSiteLocale, unsupportedLocaleMessage } from "../shared/siteLanguages"
import { ulid } from "ulid"
import {
  type KBStore, type AnyNode, type CategoryNode, type SectionNode, type ArticleNode,
  type Translation, type PublishState, type NodeKind, emptyStore, articleState, localesByState, nodeTitle,
} from "./types"

const KEY_PREFIX = "kb-cms:"
const DEFAULT_KB = "default"

function keyFor(kbId: string) { return `${KEY_PREFIX}${kbId}` }


/**
 * A fresh, empty store.
 *
 * There is no local cache: the hook pulls the real store from Postgres on
 * mount and on every team switch, so anything held here would be a third
 * copy of state that React and the store ref already own -- and one that
 * silently went stale on a reload.
 */
export function emptyStoreFor(): KBStore {
  return emptyStore()
}


// ── Mutations ────────────────────────────────────────────────────────────────
// Each returns a NEW store object so React state updates propagate.

function clone(store: KBStore): KBStore {
  return JSON.parse(JSON.stringify(store))
}

function now() { return Date.now() }

function mkTranslation(
  locale: string, title: string, body = "", description = "", state?: PublishState
): Translation {
  return { locale, title, body, description, reviewed: false, state, updatedAt: now() }
}

function nextPosition(siblings: { position: number }[]): number {
  return siblings.length === 0 ? 0 : Math.max(...siblings.map(s => s.position)) + 1
}

// A language the knowledge base doesn't list yet has to be one the sites can show
function assertNewLocaleSupported(store: KBStore, locale: string) {
  if (!store.locales.includes(locale) && !isSiteLocale(locale)) throw new Error(unsupportedLocaleMessage(locale))
}

export function ensureLocale(store: KBStore, locale: string): KBStore {
  if (store.locales.includes(locale)) return store
  assertNewLocaleSupported(store, locale)
  const next = clone(store)
  next.locales.push(locale)
  return next
}

/** Every translatable node in the KB, for locale-usage checks. */
function allNodes(store: KBStore) {
  return [
    ...Object.values(store.categories),
    ...Object.values(store.sections),
    ...Object.values(store.articles),
  ]
}

/** Why `locale` cannot be removed, or null when it can. */
export function localeRemovalError(store: KBStore, locale: string): string | null {
  const usage = localeUsage(
    allNodes(store),
    locale,
    (n) => nodeTitle(n, store.defaultLocale, store.defaultLocale),
    store.defaultLocale,
  )
  return localeRemovalBlocker(usage, locale)
}

/**
 * Removes a locale from the configured list.
 *
 * Refuses when the locale is still in use or is the default -- see
 * shared/localeUsage.ts for why silently orphaning translations is worse
 * than refusing.
 */
export function removeLocale(store: KBStore, locale: string): KBStore {
  const blocker = localeRemovalError(store, locale)
  if (blocker) throw new Error(blocker)
  if (!store.locales.includes(locale)) return store

  const next = clone(store)
  next.locales = next.locales.filter(l => l !== locale)
  /*
   * Also strip any empty translation stubs for this locale. They hold no
   * content (localeUsage ignores them, which is why removal was allowed),
   * but leaving them means re-adding the locale later resurrects rows that
   * look like translations and are not.
   */
  for (const node of allNodes(next)) {
    if (node.translations?.[locale]) delete node.translations[locale]
  }
  return next
}

export function createCategory(store: KBStore, title: string, locale?: string, description = ""): { store: KBStore; id: string } {
  const next = clone(store)
  const loc = locale || next.defaultLocale
  const id = ulid()
  next.categories[id] = {
    id, kind: "category", position: nextPosition(Object.values(next.categories)),
    translations: { [loc]: mkTranslation(loc, title, "", description) },
    sourceLocale: loc, createdAt: now(), updatedAt: now(),
  }
  if (!next.locales.includes(loc)) next.locales.push(loc)
  return { store: next, id }
}

export function createSection(
  store: KBStore,
  args: { title: string; categoryId: string; parentSectionId?: string | null; locale?: string; description?: string }
): { store: KBStore; id: string } {
  const next = clone(store)
  if (!next.categories[args.categoryId]) throw new Error(`No such category: ${args.categoryId}`)
  if (args.parentSectionId && !next.sections[args.parentSectionId]) {
    throw new Error(`No such parent section: ${args.parentSectionId}`)
  }
  const loc = args.locale || next.defaultLocale
  const id = ulid()
  const siblings = Object.values(next.sections).filter(
    s => s.categoryId === args.categoryId && (s.parentSectionId ?? null) === (args.parentSectionId ?? null)
  )
  next.sections[id] = {
    id, kind: "section", categoryId: args.categoryId,
    parentSectionId: args.parentSectionId ?? null,
    position: nextPosition(siblings),
    translations: { [loc]: mkTranslation(loc, args.title, "", args.description || "") },
    sourceLocale: loc, createdAt: now(), updatedAt: now(),
  }
  if (!next.locales.includes(loc)) next.locales.push(loc)
  return { store: next, id }
}

export function createArticle(
  store: KBStore,
  args: { title: string; body?: string; sectionId: string; locale?: string; state?: PublishState; labels?: string[]; sourceRef?: string }
): { store: KBStore; id: string } {
  const next = clone(store)
  if (!next.sections[args.sectionId]) throw new Error(`No such section: ${args.sectionId}`)
  const loc = args.locale || next.defaultLocale
  const id = ulid()
  const siblings = Object.values(next.articles).filter(a => a.sectionId === args.sectionId)
  next.articles[id] = {
    id, kind: "article", sectionId: args.sectionId,
    state: args.state || "draft",
    labels: args.labels || [],
    attachments: [],
    sourceRef: args.sourceRef,
    position: nextPosition(siblings),
    translations: { [loc]: mkTranslation(loc, args.title, args.body || "", "", args.state || "draft") },
    sourceLocale: loc, createdAt: now(), updatedAt: now(),
  }
  if (!next.locales.includes(loc)) next.locales.push(loc)
  return { store: next, id }
}

function bucketFor(store: KBStore, id: string): Record<string, any> | null {
  if (store.categories[id]) return store.categories
  if (store.sections[id]) return store.sections
  if (store.articles[id]) return store.articles
  return null
}

export function getNode(store: KBStore, id: string): AnyNode | null {
  return (store.categories[id] || store.sections[id] || store.articles[id]) ?? null
}

/** Upserts one locale's translation on any node. */
export function setTranslation(
  store: KBStore,
  id: string,
  locale: string,
  patch: Partial<Pick<Translation, "title" | "body" | "description" | "reviewed" | "state">>
): KBStore {
  assertNewLocaleSupported(store, locale)
  const next = clone(store)
  const bucket = bucketFor(next, id)
  if (!bucket) throw new Error(`No such node: ${id}`)
  const node = bucket[id] as AnyNode
  const existing = node.translations[locale]
  node.translations[locale] = {
    locale,
    title: patch.title ?? existing?.title ?? "",
    body: patch.body ?? existing?.body ?? "",
    description: patch.description ?? existing?.description ?? "",
    reviewed: patch.reviewed ?? existing?.reviewed ?? false,
    // New translations start as drafts — a machine translation should never go
    // live just because the source locale is published.
    state: patch.state ?? existing?.state ?? "draft",
    updatedAt: now(),
  }
  node.updatedAt = now()
  if (!next.locales.includes(locale)) next.locales.push(locale)
  return next
}

export interface TranslationWrite {
  id: string
  locale: string
  // Replaces the locale outright; setTranslation's merge would keep old reviewed/state flags
  translation: Translation
}

// Many translation writes with one store clone. onBefore gets each node's pre-write copy for history.
// Missing nodes are skipped and reported, not thrown
export function setTranslationsBulk(
  store: KBStore,
  writes: TranslationWrite[],
  onBefore?: (write: TranslationWrite, before: AnyNode) => void,
): { store: KBStore; applied: number; missing: TranslationWrite[] } {
  const next = clone(store)
  const missing: TranslationWrite[] = []
  let applied = 0

  for (const w of writes) {
    assertNewLocaleSupported(next, w.locale)
    const bucket = bucketFor(next, w.id)
    if (!bucket) { missing.push(w); continue }
    const node = bucket[w.id] as AnyNode

    onBefore?.(w, JSON.parse(JSON.stringify(node)))

    node.translations[w.locale] = { ...w.translation, locale: w.locale, updatedAt: now() }
    node.updatedAt = now()
    if (!next.locales.includes(w.locale)) next.locales.push(w.locale)
    applied++
  }

  return { store: next, applied, missing }
}

/**
 * Sets the publish state of ONE locale of an article.
 *
 * Pass `allLocales` to publish/unpublish every existing translation at once —
 * useful for "publish everything", but the default is deliberately per-locale so
 * an untranslated or unreviewed locale can't be published by accident.
 */
export function setArticleState(
  store: KBStore, id: string, state: PublishState,
  opts: { locale?: string; allLocales?: boolean } = {}
): KBStore {
  const next = clone(store)
  const a = next.articles[id]
  if (!a) throw new Error(`No such article: ${id}`)

  const targets = opts.allLocales
    ? Object.keys(a.translations)
    : [opts.locale ?? a.sourceLocale]

  for (const loc of targets) {
    const tr = a.translations[loc]
    if (!tr) throw new Error(`Article has no ${loc} translation to publish`)
    tr.state = state
    tr.updatedAt = now()
  }

  // Keep the legacy field roughly meaningful for anything still reading it:
  // the article counts as published if any locale is.
  a.state = Object.keys(a.translations).some(l => (a.translations[l].state ?? a.state) === "published")
    ? "published" : "draft"
  a.updatedAt = now()
  return next
}

export function setArticleLabels(store: KBStore, id: string, labels: string[]): KBStore {
  const next = clone(store)
  const a = next.articles[id]
  if (!a) throw new Error(`No such article: ${id}`)
  a.labels = Array.from(new Set(labels.map(l => l.trim()).filter(Boolean)))
  a.updatedAt = now()
  return next
}

export function moveArticle(store: KBStore, id: string, sectionId: string): KBStore {
  const next = clone(store)
  const a = next.articles[id]
  if (!a) throw new Error(`No such article: ${id}`)
  if (!next.sections[sectionId]) throw new Error(`No such section: ${sectionId}`)
  a.sectionId = sectionId
  a.updatedAt = now()
  return next
}

/**
 * Reparents a section. Guards against cycles — making a section its own
 * ancestor would render the tree unwalkable and is easy for an agent to attempt.
 */
export function moveSection(
  store: KBStore,
  id: string,
  args: { categoryId?: string; parentSectionId?: string | null }
): KBStore {
  const next = clone(store)
  const s = next.sections[id]
  if (!s) throw new Error(`No such section: ${id}`)

  const targetParent = args.parentSectionId ?? null
  if (targetParent === id) throw new Error("A section cannot be its own parent")
  if (targetParent) {
    if (!next.sections[targetParent]) throw new Error(`No such parent section: ${targetParent}`)
    // Walk up from the intended parent; if we reach `id`, this would form a cycle.
    let cursor: string | null = targetParent
    const seen = new Set<string>()
    while (cursor) {
      if (cursor === id) throw new Error("Cycle detected: cannot nest a section inside its own descendant")
      if (seen.has(cursor)) break
      seen.add(cursor)
      cursor = next.sections[cursor]?.parentSectionId ?? null
    }
    s.categoryId = next.sections[targetParent].categoryId
  } else if (args.categoryId) {
    if (!next.categories[args.categoryId]) throw new Error(`No such category: ${args.categoryId}`)
    s.categoryId = args.categoryId
  }
  s.parentSectionId = targetParent
  s.updatedAt = now()
  return next
}

/** Deletes a node and everything beneath it. Returns counts for reporting. */
export function deleteNode(store: KBStore, id: string): { store: KBStore; deleted: { categories: number; sections: number; articles: number } } {
  const next = clone(store)
  const deleted = { categories: 0, sections: 0, articles: 0 }

  const dropArticlesIn = (sectionId: string) => {
    for (const a of Object.values(next.articles)) {
      if (a.sectionId === sectionId) { delete next.articles[a.id]; deleted.articles++ }
    }
  }
  const dropSectionTree = (sectionId: string) => {
    for (const child of Object.values(next.sections)) {
      if (child.parentSectionId === sectionId) dropSectionTree(child.id)
    }
    dropArticlesIn(sectionId)
    if (next.sections[sectionId]) { delete next.sections[sectionId]; deleted.sections++ }
  }

  if (next.categories[id]) {
    for (const s of Object.values(next.sections)) {
      if (s.categoryId === id && s.parentSectionId === null) dropSectionTree(s.id)
    }
    // Sweep any sections still pointing at this category (defensive against orphans).
    for (const s of Object.values(next.sections)) {
      if (s.categoryId === id) dropSectionTree(s.id)
    }
    delete next.categories[id]
    deleted.categories++
  } else if (next.sections[id]) {
    dropSectionTree(id)
  } else if (next.articles[id]) {
    delete next.articles[id]
    deleted.articles++
  } else {
    throw new Error(`No such node: ${id}`)
  }

  return { store: next, deleted }
}

/*
 * Read-only views live in ./queries.ts but are re-exported here: callers
 * import this module as a namespace (`db.pathOf(...)`), so re-exporting keeps
 * that surface unchanged and the split required no call-site changes.
 */
export * from "./queries"
